import { useEffect, useRef, useState } from 'react'
import { CloseIcon, SendIcon, SparklesIcon } from './icons.jsx'
import ApolloSendDialog from './ApolloSendDialog.jsx'
import { onCompose, setComposeDockHeight } from '../lib/compose.js'
import { gmailDraft } from '../lib/gmail.js'
import { logEvent } from '../lib/events.js'
import { useAuth } from '../auth.jsx'

// Gmail-style compose window, mounted once in the Shell. Opens bottom-right on
// openCompose(), keeps working while the user navigates, minimizes to a title
// bar. SENDING IS APOLLO ONLY (2026-10-20, the owner: "needs to be through
// Apollo completely"): Send opens the Apollo dialog — the mailbox it goes from,
// now or scheduled — and the connected Gmail inbox is never used to send. A
// reply goes through Apollo too, as a new email carrying the "Re:" subject: an
// Apollo one-off email cannot join a Gmail thread. "Draft with AI" is OpenAI
// only and needs no inbox.
const FIELD =
  'w-full border-none bg-transparent px-4 py-[9px] text-[13.5px] text-ink placeholder:text-faint focus:outline-none'
const HAIRLINE = '[border-bottom:1px_solid_var(--table-line)]'

// Dock footprint the toast stack has to clear (see lib/compose.js).
const OPEN_H = 528
const MINI_H = 52
const EXPANDED_H = 660

export default function ComposeDock() {
  const { user } = useAuth()
  const [draft, setDraft] = useState(null) // null = closed
  const [minimized, setMinimized] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState('')
  const [aiOpen, setAiOpen] = useState(false)
  const [purpose, setPurpose] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [apolloOpen, setApolloOpen] = useState(false)
  // One send identity per opened compose window, so pressing Send twice on the
  // same message is refused server-side, while the same subject to the same
  // person from a NEW compose window is a new email.
  const composeKey = useRef('')
  const bodyRef = useRef(null)

  // Any openCompose() call takes over the window — one compose at a time, like
  // a docked mail client. A draft in progress is preserved only if it's empty.
  useEffect(
    () =>
      onCompose((msg) => {
        setDraft(msg)
        composeKey.current = `compose:${msg.id}:${Date.now()}`
        setApolloOpen(false)
        setTo(msg.to || '')
        setSubject(msg.subject || '')
        setBody(msg.body || '')
        setPurpose(msg.purpose || '')
        setError('')
        setMinimized(false)
        setAiOpen(false)
      }),
    [],
  )

  // Publish the footprint so toasts float above the dock instead of covering it.
  useEffect(() => {
    if (!draft) {
      setComposeDockHeight(0)
      return
    }
    setComposeDockHeight(minimized ? MINI_H : expanded ? EXPANDED_H : OPEN_H)
  }, [draft, minimized, expanded])

  useEffect(() => {
    if (!draft || minimized) return
    // A reply already knows its recipient and subject — land the cursor in the
    // message so the rep can just type.
    if (draft.mode === 'reply') bodyRef.current?.focus()
  }, [draft, minimized])

  function close() {
    setDraft(null)
    setTo('')
    setSubject('')
    setBody('')
    setPurpose('')
    setError('')
    setAiOpen(false)
    setExpanded(false)
  }

  async function handleDraftWithAi() {
    setError('')
    setDrafting(true)
    try {
      const res = await gmailDraft({
        to: to.trim(),
        company: draft.company || '',
        recipient_name: draft.contactName || '',
        city: draft.city || '',
        trade: draft.trade || '',
        sender_name: user?.name || '',
        purpose: purpose.trim(),
      })
      if (res.configured === false) {
        setError('AI drafting isn’t set up yet — add the OpenAI key in Settings.')
        return
      }
      if (res.subject && draft.mode !== 'reply') setSubject(res.subject)
      if (res.body) setBody(res.body)
      setAiOpen(false)
      logEvent('email_drafted', { detail: `Drafted an email to ${to.trim() || draft.company || 'a lead'}` })
    } catch (err) {
      setError(err.message)
    } finally {
      setDrafting(false)
    }
  }

  // Send = the Apollo dialog. Nothing leaves until the rep confirms there; the
  // dialog logs the send and the sender lambda records the delivery.
  function handleSend() {
    setError('')
    const address = to.trim()
    if (!address || !address.includes('@')) {
      setError('Add a recipient email address.')
      return
    }
    if (!subject.trim() || !body.trim()) {
      setError('Write a subject and a message before sending.')
      return
    }
    setApolloOpen(true)
  }

  // ⌘↵ opens the send dialog. Escape minimizes rather than losing the draft.
  function onKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSend()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setMinimized(true)
    }
  }

  if (!draft) return null

  const title = minimized
    ? subject.trim() || (draft.mode === 'reply' ? 'Reply' : 'New message')
    : draft.mode === 'reply'
      ? `Reply${draft.company ? ` · ${draft.company}` : ''}`
      : `New message${draft.company ? ` · ${draft.company}` : ''}`
  const sameRecipient = to.trim().toLowerCase() === String(draft.to || '').trim().toLowerCase()

  return (
    <>
    <div
      className={`fixed bottom-0 right-6 z-[125] flex flex-col overflow-hidden rounded-t-[12px] border border-line bg-surface shadow-[0_-4px_40px_rgba(0,0,0,0.28)] animate-[toastIn_0.18s_ease] ${
        minimized
          ? 'h-[52px] w-[320px]'
          : expanded
            ? 'h-[660px] w-[min(720px,calc(100vw-64px))]'
            : 'h-[528px] w-[min(460px,calc(100vw-48px))]'
      }`}
      role="dialog"
      aria-label={title}
      onKeyDown={onKeyDown}
    >
      {/* Title bar — dark like Gmail's, doubles as the minimize toggle */}
      <div
        className="flex flex-none cursor-pointer items-center gap-x-2 bg-[#151515] px-4 py-[14px] dark:bg-panel"
        onClick={(e) => {
          if (e.target.closest('button')) return
          setMinimized((v) => !v)
        }}
      >
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-white dark:text-ink">{title}</span>
        <button
          type="button"
          aria-label={minimized ? 'Expand' : 'Minimize'}
          onClick={() => setMinimized((v) => !v)}
          className="grid h-[22px] w-[22px] flex-none place-items-center rounded-[6px] border-none bg-transparent text-[rgba(255,255,255,0.55)] transition-colors hover:bg-[rgba(255,255,255,0.12)] hover:text-white dark:text-dim dark:hover:bg-surface dark:hover:text-ink"
        >
          {/* coolicons: line/Chevron_Up.svg and line/Remove_Minus.svg — 24-grid,
              so the viewBox moves with them; the 1.8 stroke is unchanged. */}
          <svg viewBox="0 0 24 24" className="h-[13px] w-[13px]" aria-hidden="true">
            {minimized ? (
              <path d="M5 16L12 9L19 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <path d="M6 12H18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            )}
          </svg>
        </button>
        {!minimized && (
          <button
            type="button"
            aria-label={expanded ? 'Shrink' : 'Full screen'}
            onClick={() => setExpanded((v) => !v)}
            className="grid h-[22px] w-[22px] flex-none place-items-center rounded-[6px] border-none bg-transparent text-[rgba(255,255,255,0.55)] transition-colors hover:bg-[rgba(255,255,255,0.12)] hover:text-white dark:text-dim dark:hover:bg-surface dark:hover:text-ink"
          >
            {/* coolicons: line/Shrink.svg and line/Expand.svg */}
            <svg viewBox="0 0 24 24" className="h-[12px] w-[12px]" fill="none" aria-hidden="true">
              <path
                d={expanded ? 'M5 14H10V19M19 10H14V5' : 'M10 19H5V14M14 5H19V10'}
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <button
          type="button"
          aria-label="Discard draft"
          onClick={close}
          className="grid h-[22px] w-[22px] flex-none place-items-center rounded-[6px] border-none bg-transparent text-[rgba(255,255,255,0.55)] transition-colors hover:bg-[rgba(255,255,255,0.12)] hover:text-white dark:text-dim dark:hover:bg-surface dark:hover:text-ink [&_svg]:h-[13px] [&_svg]:w-[13px]"
        >
          <CloseIcon />
        </button>
      </div>

      {!minimized && (
        <>
          <div className={`flex flex-none items-center ${HAIRLINE}`}>
            <span className="flex-none pl-4 text-[12.5px] text-faint">To</span>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="name@company.com"
              autoFocus={draft.mode !== 'reply'}
              className={FIELD}
            />
          </div>
          <div className={`flex-none ${HAIRLINE}`}>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className={FIELD}
            />
          </div>

          {aiOpen && (
            <div className={`flex flex-none items-center gap-x-2 bg-panel px-4 py-2.5 ${HAIRLINE}`}>
              <input
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleDraftWithAi()
                  }
                }}
                autoFocus
                placeholder="What should it say? e.g. intro + ask for a 15-min call"
                className="w-full flex-1 rounded-[8px] border border-line bg-surface px-3 py-[7px] text-[13px] text-ink placeholder:text-faint focus:border-primary focus:outline-none"
              />
              <button
                type="button"
                onClick={handleDraftWithAi}
                disabled={drafting}
                className="flex-none rounded-[8px] bg-primary px-3 py-[7px] text-[12.5px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {drafting ? 'Writing…' : 'Write it'}
              </button>
            </div>
          )}

          <textarea
            ref={bodyRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message…"
            className="min-h-0 flex-1 resize-none border-none bg-transparent px-4 py-3 text-[13.5px] leading-[1.6] text-ink placeholder:text-faint focus:outline-none"
          />

          {error && (
            <div className="flex-none px-4 pb-1 text-[12.5px] text-[#dc2626] dark:text-[#fca5a5]">{error}</div>
          )}

          <div className="flex flex-none items-center gap-x-2 px-4 py-3">
            <button
              type="button"
              onClick={handleSend}
              title="Send through Apollo — now or scheduled (⌘↵)"
              className="inline-flex flex-none items-center gap-x-1.5 rounded-full bg-primary px-5 py-[9px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SendIcon className="h-[14px] w-[14px]" />
              Send
            </button>
            <button
              type="button"
              onClick={() => setAiOpen((v) => !v)}
              title="Draft this email with AI"
              className={`inline-flex flex-none items-center gap-x-1.5 rounded-[9px] border border-line px-3 py-2 text-[12.5px] font-semibold transition-colors ${
                aiOpen ? 'bg-primary-tint text-primary-dark' : 'bg-surface text-dim hover:bg-panel hover:text-ink'
              }`}
            >
              <SparklesIcon className="h-[15px] w-[15px]" />
              Draft with AI
            </button>
            <span className="ml-auto flex-none text-[11.5px] text-faint">Sends through Apollo · ⌘↵</span>
          </div>
        </>
      )}
    </div>
    {/* Outside the dock: the dock is an animated, overflow-hidden box, and a
        fixed dialog inside it would be clipped to it. */}
    {apolloOpen && (
        <ApolloSendDialog
          layerClass="z-[130]"
          title={`Send to ${(sameRecipient && draft.contactName) || to.trim()} via Apollo`}
          emails={[
            {
              to: to.trim(),
              // The lead's contact name belongs to the address the window was
              // opened with. Retyped to somebody else, it must not travel with
              // them — Apollo would file that name on the new address.
              to_name: sameRecipient ? draft.contactName || '' : '',
              company: sameRecipient ? draft.company || '' : '',
              kind: draft.mode === 'reply' ? 'reply' : 'compose',
              draft_key: composeKey.current,
              subject: subject.trim(),
              body_text: body,
            },
          ]}
          onClose={() => setApolloOpen(false)}
          onSent={close}
        />
      )}
    </>
  )
}
